/**
 * The accounts API of a server (`/api/...`): signing up and in, teams, their spaces, members,
 * invites and preset libraries (your own, a team's). Every call but signing up/in carries the session token (`Authorization: Bearer`).
 *
 * The GUI can be signed in to several servers at once (the page's own server, a team's hosted
 * one, a studio's): {@link loadSessions} keeps one session per server in this browser.
 */
import type { Account } from "@protocol/Account";
import type { AccountExport } from "@protocol/AccountExport";
import type { ActivityEntry } from "@protocol/ActivityEntry";
import type { RecoveryCodes } from "@protocol/RecoveryCodes";
import type { SecondStepSetup } from "@protocol/SecondStepSetup";
import type { SessionInfo } from "@protocol/SessionInfo";
import type { SpaceTicket } from "@protocol/SpaceTicket";
import type { AuthProvider } from "@protocol/AuthProvider";
import type { InviteInfo } from "@protocol/InviteInfo";
import type { Me } from "@protocol/Me";
import type { PresetEdit } from "@protocol/PresetEdit";
import type { PresetLibrary } from "@protocol/PresetLibrary";
import type { Role } from "@protocol/Role";
import type { Session } from "@protocol/Session";
import type { SpaceInfo } from "@protocol/SpaceInfo";
import type { SpaceSummary } from "@protocol/SpaceSummary";
import type { TeamInvite } from "@protocol/TeamInvite";
import type { TeamSummary } from "@protocol/TeamSummary";
import type { TeamView } from "@protocol/TeamView";
import { load, save } from "../shared/storage";

/** Where the sessions are kept. */
export const SESSIONS_KEY = "developer-app-sessions";

/** Signed in to one server. */
export interface SignedIn {
  /** The server, `http(s)://host[:port]`. */
  server: string;
  /** The session token. */
  token: string;
  /** Who. */
  account: Account;
}

/** Every session this browser keeps, oldest first. */
export function loadSessions(): SignedIn[] {
  return load<SignedIn[]>(SESSIONS_KEY, []);
}

/** Keeps `sessions`. */
export function saveSessions(sessions: SignedIn[]): void {
  save(SESSIONS_KEY, sessions);
}

/** The session for `server`, if this browser is signed in there. */
export function sessionFor(server: string): SignedIn | undefined {
  return loadSessions().find((s) => s.server === server);
}

/** A refusal of the API: the HTTP status and the server's reason. */
export class ApiError extends Error {
  /** The HTTP status (401: signed out, 403: role too low, 404, 409: email taken, 429: too many tries...). */
  readonly status: number;
  /** Whether the password was right and the server asks for the second step: a code from the authenticator app. */
  readonly secondStep: boolean;

  /** A refusal with `status` saying `message`. */
  constructor(status: number, message: string, secondStep = false) {
    super(message);
    this.status = status;
    this.secondStep = secondStep;
  }
}

/** How long an invite lasts and how many may use it (`days: 0`: until withdrawn; no `maxUses`: anyone with the link). */
export interface InviteLimits {
  /** Days it works for (the server's default when left out). */
  days?: number;
  /** How many people may join with it. */
  maxUses?: number;
}

/** Whether `error` is an {@link ApiError}. */
export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/** Sends one request (the browser's `fetch`; tests give a fake). */
export type Fetch = (url: string, init: RequestInit) => Promise<Pick<Response, "ok" | "status" | "json">>;

/** The calls of one server's API, as one person (`token`; none to sign up or in). */
export interface AccountsApi {
  /** Makes an account and signs in. */
  signUp(email: string, name: string, password: string): Promise<Session>;
  /** Signs in (`code`: the second step's, when the account asks for one). */
  signIn(email: string, password: string, code?: string): Promise<Session>;
  /** Ends this session. */
  signOut(): Promise<void>;
  /** The account and its teams. */
  me(): Promise<Me>;
  /** Makes a team (you own it). */
  createTeam(name: string): Promise<TeamSummary>;
  /** A team as you see it. */
  team(id: string): Promise<TeamView>;
  /** Makes a space in a team (editors and owners). */
  createSpace(team: string, name: string): Promise<SpaceSummary>;
  /** Owners: a new invite. */
  invite(team: string, role: Role, once: boolean, limits?: InviteLimits): Promise<TeamInvite>;
  /** Owners: what happened in the team lately, newest first. */
  teamActivity(team: string): Promise<ActivityEntry[]>;
  /** Owners: withdraws an invite. */
  revoke(team: string, code: string): Promise<void>;
  /** Owners: changes a member's role. */
  setRole(team: string, member: string, role: Role): Promise<void>;
  /** Owners remove someone; anyone may remove themselves (leave). */
  removeMember(team: string, member: string): Promise<void>;
  /** What an invite leads to. */
  inviteInfo(code: string): Promise<InviteInfo>;
  /** Joins an invite's team. */
  accept(code: string): Promise<TeamSummary>;
  /** A space, its team and your role there (members). */
  space(id: string): Promise<SpaceInfo>;
  /** Your own preset library. */
  personalPresets(): Promise<PresetLibrary>;
  /** Changes your own preset library; gives it after the change. */
  editPersonalPresets(edit: PresetEdit): Promise<PresetLibrary>;
  /** A team's preset library (members). */
  teamPresets(team: string): Promise<PresetLibrary>;
  /** Changes a team's preset library (editors and owners); gives it after the change. */
  editTeamPresets(team: string, edit: PresetEdit): Promise<PresetLibrary>;
  /** The services this server signs in with (Google, GitHub; none: passwords only). */
  providers(): Promise<AuthProvider[]>;
  /** Trades the ticket a sign-in with a service came back with for its session (once; `code`: the second step's). */
  finishSignIn(ticket: string, code?: string): Promise<Session>;
  /** A one-minute, one-time pass into a space's WebSocket. */
  spaceTicket(space: string): Promise<SpaceTicket>;
  /** Your signed-in devices. */
  sessions(): Promise<SessionInfo[]>;
  /** Signs one device out. */
  endSession(id: string): Promise<void>;
  /** Signs every other device out; says how many. */
  signOutOthers(): Promise<{ ended: number }>;
  /** A new password (`old` empty for an account that had none); the other devices are signed out. */
  changePassword(old: string, password: string): Promise<void>;
  /** Starts turning the second step on: the secret for the app. */
  startSecondStep(): Promise<SecondStepSetup>;
  /** Turns it on with a first code from the app; gives the recovery codes. */
  confirmSecondStep(code: string): Promise<RecoveryCodes>;
  /** Turns it off (a code from the app, or a recovery code). */
  stopSecondStep(code: string): Promise<void>;
  /** New recovery codes (the old ones stop working). */
  newRecoveryCodes(code: string): Promise<RecoveryCodes>;
  /** What happened to your account lately, newest first. */
  activity(): Promise<ActivityEntry[]>;
  /** Everything the server keeps about you. */
  exportAccount(): Promise<AccountExport>;
  /** Deletes your account (your password, or your email when it has none; `code` with a second step). */
  deleteAccount(password: string, code?: string): Promise<void>;
}

/** Where the browser goes to sign in to `server` with `provider`, coming back to `next` (a path there). */
export function signInWithUrl(server: string, provider: string, next: string): string {
  return `${server}/api/auth/${encodeURIComponent(provider)}/start?next=${encodeURIComponent(next)}`;
}

/** The API of `server` (`http(s)://host[:port]`), as the person signed in with `token`. */
export function accountsApi(server: string, token = "", fetcher: Fetch = (url, init) => fetch(url, init)): AccountsApi {
  /** One call: the answer's JSON, or an {@link ApiError}. */
  async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = {};
    if (body !== undefined) headers["Content-Type"] = "application/json";
    if (token) headers.Authorization = `Bearer ${token}`;
    let answer: Pick<Response, "ok" | "status" | "json">;
    try {
      answer = await fetcher(`${server}${path}`, { method, headers, body: body === undefined ? undefined : JSON.stringify(body) });
    } catch {
      throw new ApiError(0, `cannot reach ${server}: is the address right, and the server running?`);
    }
    if (answer.status === 204) return undefined as T;
    const json = (await answer.json().catch(() => ({}))) as T & { error?: string; second_step?: boolean };
    if (!answer.ok) throw new ApiError(answer.status, json.error ?? `the server answered ${answer.status}`, json.second_step === true);
    return json;
  }
  /** A path segment, escaped. */
  const at = (part: string) => encodeURIComponent(part);
  return {
    signUp: (email, name, password) => call("POST", "/api/account/signup", { email, name, password }),
    signIn: (email, password, code) => call("POST", "/api/account/signin", code ? { email, password, code } : { email, password }),
    signOut: () => call("POST", "/api/account/signout"),
    me: () => call("GET", "/api/account"),
    createTeam: (name) => call("POST", "/api/teams", { name }),
    team: (id) => call("GET", `/api/teams/${at(id)}`),
    createSpace: (team, name) => call("POST", `/api/teams/${at(team)}/spaces`, { name }),
    invite: (team, role, once, limits = {}) =>
      call("POST", `/api/teams/${at(team)}/invites`, { role, once, ...(limits.days === undefined ? {} : { days: limits.days }), ...(limits.maxUses ? { max_uses: limits.maxUses } : {}) }),
    teamActivity: (team) => call("GET", `/api/teams/${at(team)}/activity`),
    revoke: (team, code) => call("DELETE", `/api/teams/${at(team)}/invites/${at(code)}`),
    setRole: (team, member, role) => call("PUT", `/api/teams/${at(team)}/members/${at(member)}`, { role }),
    removeMember: (team, member) => call("DELETE", `/api/teams/${at(team)}/members/${at(member)}`),
    inviteInfo: (code) => call("GET", `/api/invites/${at(code)}`),
    accept: (code) => call("POST", `/api/invites/${at(code)}/accept`),
    space: (id) => call("GET", `/api/spaces/${at(id)}`),
    personalPresets: () => call("GET", "/api/account/presets"),
    editPersonalPresets: (edit) => call("POST", "/api/account/presets", edit),
    teamPresets: (team) => call("GET", `/api/teams/${at(team)}/presets`),
    editTeamPresets: (team, edit) => call("POST", `/api/teams/${at(team)}/presets`, edit),
    providers: () => call("GET", "/api/auth/providers"),
    finishSignIn: (ticket, code) => call("POST", "/api/auth/finish", code ? { ticket, code } : { ticket }),
    spaceTicket: (space) => call("POST", `/api/spaces/${at(space)}/ticket`),
    sessions: () => call("GET", "/api/account/sessions"),
    endSession: (id) => call("DELETE", `/api/account/sessions/${at(id)}`),
    signOutOthers: () => call("POST", "/api/account/signout-others"),
    changePassword: (old, password) => call("POST", "/api/account/password", { old, new: password }),
    startSecondStep: () => call("POST", "/api/account/second-step"),
    confirmSecondStep: (code) => call("POST", "/api/account/second-step/confirm", { code }),
    stopSecondStep: (code) => call("DELETE", "/api/account/second-step", { code }),
    newRecoveryCodes: (code) => call("POST", "/api/account/second-step/recovery", { code }),
    activity: () => call("GET", "/api/account/activity"),
    exportAccount: () => call("GET", "/api/account/export"),
    deleteAccount: (password, code) => call("DELETE", "/api/account", code ? { password, code } : { password }),
  };
}
